<script setup>
import { callWithNuxt } from '#app'
import Error404 from '#common/components/Error/Error404'
import PaginationControls from '#common/components/PaginationControls.vue'
import ga4 from '#common/utils/ga4'
import { getStoreTitle } from '#common/utils/seo'
import { pushEvent } from '#common/utils/user'
import { filtersToUrl, urlToFilters } from '#wa/utils/filters/filters'
import { getFullRegionSlug } from '#wa/utils/filters/regionSlugs'
import { fetchMissingWineries } from '#wa/utils/filters/wineries'

definePageMeta({
  keepalive: false,
  layoutTransition: false,
  pageTransition: false,
})

const { $auth, $store, $axios } = useNuxtApp()
const metadata = useMetadata()
const productResults = useProductResults()
const route = useRoute()

const { isLoading } = storeToRefs(useLayout())

const saveSearchShowModal = computed(() => {
  return $store.getters.saveSearchShowModal
})
const savedSearchEditId = computed(() => {
  return $store.getters.savedSearchEditId
})

const savedSearchEnabled = computed(() => {
  return $auth.loggedIn && metadata.constance.S3_SAVED_SEARCH_ENABLED === 'True'
})

const showHeader = computed(() => {
  return !!(
    productResults.categoryCircles ||
    (isOccasion.value && !occasionEnded.value) ||
    isOffer.value ||
    plpHeaderTitle.value
  )
})
const offerH1Tag = computed(() => {
  const data = metadata.seoForPath(route.path)
  if (!data) return 'Limited Time Offers'
  return data.h1_tag
})
const urlFilters = computed(() => urlToFilters({ route: route }))
const selectedFiltersFromUrl = computed(() =>
  urlFilters.value.filter((f) => !['ordering', 'page'].includes(f.category)),
)
const plpHeaderTitle = computed(() =>
  getStoreTitle(selectedFiltersFromUrl.value, metadata.selectedRegionChips),
)
const selectedOrderingFromUrl = computed(() =>
  productResults.orderingFromUrl(route, selectedFiltersFromUrl.value),
)
/* An inactive occasion may not have "ended" as in taken place and gone
         past the end date, but the verbage on the page is the same regardless
         when an occasion is not active that is "has ended". */
const occasionEnded = computed(
  () =>
    isOccasion.value &&
    !productResults.thisOccasion.is_active_or_current_and_hidden,
)

const isOccasion = computed(
  () =>
    route.params.category === 'occasion' ||
    (route.query.occasions !== undefined &&
      route.query.occasions.split(/,|__/).length === 1),
)

const isOffer = computed(
  () => route.params.category === 'is_offer' && route.params.slug === 'true',
)

const { status, error, refresh } = useAsyncData(
  `productList-${route.fullPath}`,
  async () => {
    isLoading.value = true
    const nuxtApp = useNuxtApp()
    try {
      if (savedSearchEnabled.value) {
        await $store.dispatch('getSavedSearches')
      }

      metadata.setSelectedRegionsFromUrl({ route })

      if (route.params.category === 'pairing') {
        callWithNuxt(nuxtApp, navigateTo, [
          route.path.replace('pairing', 'food_pairings'),
          { redirectCode: 302 },
        ])
        return null
      }

      if (
        route.params.category === 'occasion' ||
        route.query.occasions ||
        route.query.occasions__in
      ) {
        await productResults.fetchOccasion($axios, route).catch(() => {
          throw new Error('Occasion not found')
        })
      } else {
        productResults.clearOccasion()
      }

      if (
        route.params.category === 'wineries' ||
        route.query.wineries ||
        route.query.wineries__in
      ) {
        await fetchMissingWineries({ $axios }, { route })
      }

      const url = filtersToUrl(urlFilters.value, false, true)
      if (url !== route.fullPath) {
        callWithNuxt(nuxtApp, navigateTo, [url, { redirectCode: 302 }])
        return null
      }

      try {
        await productResults.fetchProducts($axios, route, urlFilters.value)
      } catch (e) {
        if (e.message === 'invalid_url') {
          throw new Error('Store category not found')
        }
        throw e
      }

      if (route.fullPath.includes('/store/')) {
        pushEvent(
          {
            metric_name: 'plp viewed',
            url_path: route.fullPath,
            extra_data: { search_products: productResults.searchProducts },
          },
          $store,
        )
        ga4.trackViewItemList({
          path: route.fullPath,
          products: productResults.searchProducts,
        })

        if (import.meta.client) {
          if (productResults.productIDInView !== undefined) {
            const element = document.querySelector(
              `ul[class="product-grid"] > li[data-product-id="${productResults.productIDInView}"]`,
            )
            if (element) {
              element.scrollIntoView({
                behavior: 'smooth',
                block: 'end',
                inline: 'nearest',
              })
            }
            productResults.setProductIDInView(undefined)
          }
        }
      }

      return true
    } finally {
      isLoading.value = false
    }
  },
  {},
)

const pending = computed(() => status.value === 'pending')

watch(
  () => $auth.loggedIn,
  (newValue) => {
    refresh()
  },
)

watch(route, (newValue) => {
  refresh()
})

function currentPageNumber() {
  return parseInt(route.query.page || '1')
}
function onCategoryCircleClick(categoryCircle) {
  ga4.track('visual_nav_click', categoryCircle)
}
function categoryCircleUrl(categoryCircle) {
  if (!categoryCircle) {
    return ''
  }
  let filters = [...urlFilters.value] // clone it
  if (categoryCircle.filter === 'region_slug') {
    filters.push({
      key: getFullRegionSlug({
        route: route,
        regionSlug: categoryCircle.value,
      }),
      name: categoryCircle.name,
      category: categoryCircle.filter,
    })
  } else {
    filters.push({
      key: categoryCircle.value,
      name: categoryCircle.name,
      category: categoryCircle.filter,
    })
  }
  if (categoryCircle.filter === 'varietals') {
    // remove type when adding a varietal via circles
    filters = filters.filter((f) => f.category !== 'categories')
  }
  return filtersToUrl(filters)
}
function pageUrl(nextPageNum) {
  const filters = _.cloneDeep(selectedFiltersFromUrl.value)
  const pageFilter = filters.find((f) => f.category === 'page')
  if (pageFilter) {
    pageFilter.page = nextPageNum
    pageFilter.key = nextPageNum
  } else {
    filters.push({ category: 'page', key: nextPageNum, page: nextPageNum })
  }
  const orderingFilter = filters.find((f) => f.category === 'ordering')
  if (selectedOrderingFromUrl.value && !orderingFilter) {
    filters.push({ category: 'ordering', key: selectedOrderingFromUrl.value })
  }
  return filtersToUrl(filters)
}
useHead(() => {
  const selectedFilters = selectedFiltersFromUrl.value
  const categoryParam = route.params.category
  const searchStr =
    categoryParam === 'search' && selectedFilters.length !== 1
      ? ' | Search'
      : ''
  const filterStr =
    selectedFilters.length === 1 &&
    selectedFilters[0] &&
    selectedFilters[0].name
      ? ` | ${selectedFilters[0].name}`
      : ''
  const meta = [
    {
      hid: 'og:title',
      name: 'og:title',
      property: 'og:title',
      content: `The Wine Access Store${searchStr}${filterStr}`,
    },
  ]
  if (categoryParam === 'occasion' && productResults.thisOccasion.description) {
    meta.push({
      hid: 'og:description',
      name: 'og:description',
      property: 'og:description',
      content: productResults.thisOccasion.description,
    })
  }
  if (
    route.name === 'store-category-slug' &&
    (route.params.category === 'search' ||
      route.params.category === 'notify-me' ||
      route.params.category === 'personalized')
  ) {
    meta.push({
      hid: 'robots',
      name: 'robots',
      content: 'noindex, nofollow',
    })
  }
  return {
    meta,
  }
})
</script>

<template>
  <div v-if="error" class="nuxt-error">
    <Error404 :message="error" />
  </div>
  <section
    v-else
    class="products"
    :class="{ searching: productResults.searchingProducts }"
  >
    <LearnContent
      v-if="route.params.category === 'wineries'"
      :key="
        route.params.category
          ? `${route.params.category}-${route.params.slug}-top`
          : 'learn-top'
      "
      :start-expanded="true"
    />
    <ProductSavedSearch
      v-if="savedSearchEnabled"
      :selected-filters="selectedFiltersFromUrl"
      :show="saveSearchShowModal"
      :edit-search="savedSearchEditId"
    />
    <header class="category-header">
      <div
        v-if="showHeader"
        class="category-header-content"
        :class="{ 'category-header-pending': pending }"
      >
        <h1 v-if="isOccasion && !occasionEnded">
          {{ productResults.thisOccasion.name }}
        </h1>
        <h1 v-else-if="isOffer">
          {{ offerH1Tag }}
        </h1>
        <h1 v-else-if="plpHeaderTitle">
          {{ plpHeaderTitle }}
        </h1>
        <div v-if="productResults.categoryCircles?.description" class="copy">
          <p>
            {{ productResults.categoryCircles.description }}
          </p>
        </div>
        <div
          v-else-if="
            isOccasion &&
            !occasionEnded &&
            productResults.thisOccasion?.description
          "
          class="copy"
        >
          <p>{{ productResults.thisOccasion.description }}</p>
        </div>
        <div
          v-if="productResults.categoryCircles?.length"
          class="category-selector"
        >
          <ul class="categories">
            <li
              v-for="categoryCircle in productResults.categoryCircles"
              :key="categoryCircle && categoryCircle.name"
            >
              <nuxt-link
                v-if="categoryCircle"
                no-prefetch
                :to="categoryCircleUrl(categoryCircle)"
                @click="onCategoryCircleClick(categoryCircle)"
              >
                <span class="category-image">
                  <img
                    :src="
                      categoryCircle.highest_price_wine
                        .bottle_image_optimized_url
                    "
                    alt=""
                  />
                </span>
                <span v-if="categoryCircle" class="category-name">{{
                  categoryCircle.name
                }}</span>
              </nuxt-link>
            </li>
          </ul>
        </div>
      </div>
    </header>
    <ProductSideFilter>
      <template v-if="!pending">
        <div v-if="occasionEnded" class="row">
          <div class="columns store-unavailable">
            <h2>This Event Has Ended</h2>
            <p>
              Don't worry! We still have other amazing wines in our
              <nuxt-link to="/store/">Store.</nuxt-link>
            </p>
            <nuxt-link to="/store/"><h2>Go to the Store!</h2></nuxt-link>
          </div>
        </div>
        <template v-else-if="productResults.searchProducts.length < 1">
          <div v-if="route.params.category === 'notify-me'" class="row">
            <div class="columns store-unavailable">
              <h2 v-if="!$auth.loggedIn">
                Please
                <a class="signin-inline" @click="$auth.openAuthModal()"
                  >Sign In</a
                >
                to see your subscribed products.
              </h2>
              <h2 v-else>No subscribed products available.</h2>
            </div>
          </div>
          <div v-else class="row">
            <div class="columns store-unavailable">
              <h2>No Products Match Selected Filters</h2>
              <p>
                You can change your selected filters above or head back to the
                <nuxt-link to="/store/">Store.</nuxt-link>
              </p>
            </div>
          </div>
        </template>
      </template>
      <ProductGrid
        v-if="!occasionEnded"
        :items="productResults.searchProducts"
        :enable-qes-section="isOffer"
      />
      <PaginationControls
        v-if="!occasionEnded"
        :total-pages="productResults.totalPages"
        :current-page-number="currentPageNumber()"
        :url-for-page-func="pageUrl"
      />
    </ProductSideFilter>
    <LearnContent
      v-if="route.params.category !== 'wineries'"
      :key="
        route.params.category
          ? `${route.params.category}-${route.params.slug}`
          : 'learn'
      "
      :start-expanded="productResults.searchProducts.length < 1"
    />
  </section>
</template>
